import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Modal,
  Paper,
  SvgIcon,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import Select from "react-select";
import { Icon } from "@iconify/react";
import SettingsIcon from '@mui/icons-material/Settings';
import { Edit, Mail, Person } from "@mui/icons-material";
import { ReactComponent as Logo } from "../../assets/svg/logo.svg";
import { makeStyles } from "@mui/styles";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { updateName } from "../../services/RevenueTargetingModels";
import { getCompanies } from "../../services/Companies";
import SnackbarContext from "../../context/SnackbarContext";
import { useCookies } from "react-cookie";

const titleModalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 280,
  backgroundColor: "background.paper",
  borderRadius: "6px",
  boxShadow: 24,
  p: 4,
};

const avatarStyle = {
  backgroundColor: "#009cf8",
};

const useStyles = makeStyles({
  editButton: {
    position: "absolute",
    marginTop: "-6px",
    marginLeft: "6px",
    visibility: "hidden",
  },
  title: {
    "&.MuiTypography-root": {
      textTransform: "none",
    },
    "&:hover $editButton": {
      visibility: "inherit",
    },
  },
});

function Layout({ sidebar, main, page, title, modelID, getModels }) {
  const [cookies, setCookie, removeCookie] = useCookies();

  const [userAccounts, setUserAccounts] = useState([]);
  const [userAccount, setUserAccount] = useState({});
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [modelTitle, setModelTitle] = useState(title);
  const [modelTitleBtnLoading, setModelTitleBtnLoading] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(0);
  const [numCompanies, setNumberCompanies] = useState(0);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);

  const theme = useTheme();
  const belowMdScreen = useMediaQuery(theme.breakpoints.down("md"));
  const belowXlScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const aboveXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  
  const drawerWidth = belowMdScreen ? 0 : belowXlScreen ? 240 : aboveXlScreen ? 300 : 300;
  
  const classes = useStyles();

  const navigate = useNavigate();

  const auth = useContext(AuthContext);

  const snackbar = useContext(SnackbarContext);

  const handleLogout = () => {
    removeCookie('token', {domain: ".revoptimal.com", path:"/"});
    removeCookie('user_id', {domain: ".revoptimal.com", path:"/"});
    if (window.location.pathname !== "/") {window.location.pathname = "/"};
  }

  useEffect(() => {
    if (modelID) {
      getCompanies(auth.token, modelID, 1, 1)
      .then((response) => {
        if (response) {
          setNumberCompanies(response.total_objects);
        }
      })
    }
  });

  useEffect(() => {
    if (auth.userAccount?.tier?.name.includes('Trial')) {
      setTrialDaysRemaining(auth.userAccount.tier.trial_days_remaining);
    } else {
      setTrialDaysRemaining(0);
    }
  }, [auth.userAccount]);

  useEffect(() => {
    if (auth.userAccount?.tier?.name.includes('Trial')) {setBannerHeight("40px")}
    else {setBannerHeight(0)};
  }, [auth.userAccount]);

  useEffect(() => {
    setModelTitle(title);
  }, [title]);

  useEffect(() => {
    if (auth.userAccounts.length > 0) {
      const currentAccount = auth.userAccounts.find(
        (a) => a.id === auth.userAccountID
      );
      setUserAccount({ value: currentAccount.id, label: currentAccount.name });
    }
    setUserAccounts(
      [...auth.userAccounts.map((a) => {
        return { value: a.id, label: a.name };
      })].sort((a, b) => a.label?.localeCompare(b.label))
    );
  }, [auth.userAccounts, auth.userAccountID]);

  const handleOpenTitleModal = () => {
    setTitleModalOpen(true);
  };

  const handleCloseTitleModal = () => setTitleModalOpen(false);

  const modelTitleBtnDisabled = () => {
    return modelTitle ? modelTitle.trim().length === 0 : true;
  };

  const handleModelTitleChange = () => {
    setModelTitleBtnLoading(true);
    updateName(auth.token, modelID, modelTitle.trim())
      .then((response) => {
        if (response.revenue_targeting_models) {
          snackbar.createSuccess("Model name change successful!");
          handleCloseTitleModal();
          getModels();
        }
      })
      .catch((error) => {
        snackbar.createError("Error changing model name.");
      })
      .finally(() => {
        setModelTitleBtnLoading(false);
      });
  };

  return (
    <>
      {auth?.userAccount?.tier?.name.includes('Trial') && (
        <AppBar
          position="fixed"
          color="primary"
          sx={{
            height: bannerHeight,
            textAlign: "center",
            justifyContent: "center"
          }}
        >
          <Typography fontWeight={"bold"}>
            {trialDaysRemaining <= 0 || !trialDaysRemaining 
              ? <>Your trial has ended. </>
              : trialDaysRemaining === 1 ? <>There is {trialDaysRemaining} day remaining on your trial! </>
              : <>There are {trialDaysRemaining} days remaining on your trial! </>}
            {numCompanies} in-market companies are waiting to hear from you!
            <Link href="https://revoptimal.com/contact/" color={"#fff"} sx={{mx:1}}>Contact us</Link>
            to unlock full access now.
          </Typography>
        </AppBar>
      )}
      <Box
        sx={{
          display: "flex",
          mt: bannerHeight
        }}
      >
        <AppBar
          color="neutral"
          position="fixed"
          sx={{
            width: `calc(100% - ${drawerWidth}px)`,
            ml: `${drawerWidth}px`,
            mt: bannerHeight
          }}
          elevation={1}
        >
          <Toolbar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                noWrap
                variant="title"
                component="h1"
                className={classes.title}
              >
                {modelTitle}
                {page === "edit" && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenTitleModal();
                    }}
                    className={classes.editButton}
                    size="small"
                    edge="end"
                    aria-label="edit"
                  >
                    <Edit />
                  </IconButton>
                )}
              </Typography>
              <Modal open={titleModalOpen} onClose={handleCloseTitleModal}>
                <Box sx={titleModalStyles}>
                  <Grid align="center">
                    <Avatar style={avatarStyle}>
                      <Icon icon="uil:channel-add" />
                    </Avatar>
                  </Grid>
                  <Stack spacing={2}>
                    <TextField
                      label="Name"
                      variant="standard"
                      value={modelTitle}
                      onChange={(event) => setModelTitle(event.target.value)}
                      placeholder="Enter name"
                      fullWidth
                      required
                    />
                    <Button
                      disabled={modelTitleBtnDisabled()}
                      onClick={handleModelTitleChange}
                      loading={modelTitleBtnLoading}
                      type="submit"
                      color="primary"
                      variant="contained"
                      fullWidth
                    >
                      Change Model Name
                    </Button>
                  </Stack>
                </Box>
              </Modal>
            </Box>
            {(auth.userAccountRole === "ADMIN" || auth.userAccountRole === "OWNER" || auth.superUser) && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/account/${auth.userAccountID}/integrations`);
                }}
                sx={{margin: "2px 12px 0 0"}}
                edge="end"
                aria-label="account settings"
              >
                <SettingsIcon fontSize="medium"/>
              </IconButton>
            )}
            {auth.userAccounts.length > 1 && (
              <Paper sx={{ width: "160px", margin: "2px 12px 0 0", boxShadow:0 }}>
                <Select
                  options={userAccounts}
                  isSearchable={false}
                  hideSelectedOptions
                  value={userAccount}
                  onChange={(o) => {
                    console.log('user change', o);
                    auth.setUserAccountID(o.value);
                  }}
                  placeholder="Select Account..."
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary25: "#b1dffb",
                      primary: "#019cf8",
                    },
                  })}
                />
              </Paper>
            )}
            <Button
              variant="outlined"
              color="neutralInverted"
              sx={{ width: 160, fontWeight: "bold" }}
              startIcon={<Person />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
          variant="permanent"
          anchor="left"
          PaperProps={{ elevation: 3, style: { background: "#f7f7f7"}, sx:{mt:bannerHeight} }}
        >
          <Toolbar sx={{ backgroundColor: "neutral.main" }}>
            <a href="https://revoptimal.com">
              <SvgIcon fontSize="large">
                <Logo color="#3d4849" />
              </SvgIcon>
            </a>
            
          </Toolbar>
          <Divider />
          <Box sx={{ backgroundColor: "neutral.main", padding: "24px" }}>
            <Typography
              sx={{ color: "#3d4849", fontSize: "1em", fontWeight: "bold" }}
            >
              {auth.username}
            </Typography>
            <Typography
              sx={{ fontSize: ".8em", fontWeight: "bold", color: "#b6c2d3" }}
            >
              {auth.userEmail}
            </Typography>
          </Box>
          {sidebar}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: `calc(100% - ${drawerWidth}px)`,
              padding: "5px 5px 25px 5px",
              textAlign: "center",
            }}
          >
            {!belowMdScreen &&
              <Button
                href="https://revoptimal.com/contact/"
                startIcon={<Mail />}
                sx={{ fontWeight: "bold" }}
                variant="text"
                size="large"
                target="_blank"
              >
                Contact Us
              </Button>
            }
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{ paddingTop: "64px !important", flexGrow: 1, p: 3 }}
        >
          {main}
        </Box>
      </Box>
    </>
  );
}

export default Layout;
